import { toast } from "react-toastify";
import firebase from "../../config/firebase";
import { RepositoryFactory } from "repository/RepositoryFactory";
let User = RepositoryFactory.get("user");
let unsubscribeSnapshot = null;

export const addNewUser =
  (payload, permissions, onSuccess = () => {}, onError = () => {}) =>
  async (dispatch) => {
    try {
      dispatch(Loader(true));
      const { data } = await User.add(payload);
      if (data.success) {
        if (Object.keys(permissions).length > 0) {
          await firebase
            .firestore()
            .collection("permissions")
            .add({
              userID: data.data.id,
              ...permissions,
            });
        }
        if (payload.type === "admin") {
          let message = payload?.role === "sub-admin" ? "Sub-admin" : "Analyst";
          dispatch({
            type: "GET_ALL_ADMINS",
            payload,
          });
        } else if (
          payload.type === "kitchen-cook" ||
          payload.type === "kitchen-admin" ||
          payload.type === "kitchen-waiter"
        ) {
          dispatch({
            type: "STAFF_ADDED",
            payload: data.data,
          });
          dispatch(Loader(false));
          toast.success("New staff added and verification email has been sent");
        } else if (payload.type === "driver") {
          dispatch({
            type: "DRIVER_ADDED",
            payload: data.data,
          });
          dispatch(Loader(false));
          toast.success(
            "New driver added and verification email has been sent"
          );
        }
        dispatch(Loader(false));
        onSuccess();
      } else {
        onError();
        toast.error(data.message);
        dispatch(Loader(false));
      }
    } catch (error) {
      onError();
      console.error(error.message);
      const errorMsg = error?.response?.data?.message || error.message;
      if (
        errorMsg?.includes("454 4.7.0") &&
        errorMsg?.includes("Too many login attempts")
      ) {
        toast.error(
          "Too many login attempts. Please try again later or check your Gmail SMTP settings."
        );
      } else {
        toast.error(
          errorMsg || "An unexpected error occurred.Please try again"
        );
      }
      dispatch(Loader(false));
    }
  };

export const login = (creds) => {
  return async (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await firebase
          .auth()
          .signInWithEmailAndPassword(creds.email, creds.password);

        const user = data.user;
        const uid = user.uid;

        const userRef = firebase.firestore().collection("users").doc(uid);
        const userDoc = await userRef.get();

        if (userDoc.exists) {
          const userData = userDoc.data();
          const status = userData.status;

          if (userData.type === "restaurant" && status === "InActive") {
            await firebase.auth().signOut();
            throw new Error("Account is inactive. Please contact support.");
          }

          // If email is verified or test admin account
          if (user.emailVerified || creds.email === "testadmin@gmail.com") {
            // 🔹 Check if user has "isVerified"
            if (!userData.isVerified) {
              await userRef.update({ isVerified: true });
            }

            dispatch(reRegisterSnapshot(uid, userData?.type))
              .then((user) => resolve(user))
              .catch((err) => reject(err));
          } else {
            throw new Error(
              "Your email is not verified. Please check your email inbox."
            );
          }
        } else {
          reject("User not found");
        }
      } catch (error) {
        if (error.message.includes("INVALID_LOGIN_CREDENTIALS")) {
          toast.warning("Invalid Credentials. Please try again.");
        } else {
          toast.warning(error.message || "An unknown error has occurred");
        }
        reject(error);
      }
    });
  };
};


export const logout = () => {
  return (dispatch) => {
    if (typeof unsubscribeSnapshot === "function") {
      unsubscribeSnapshot();
      unsubscribeSnapshot = null;
    }

    firebase
      .auth()
      .signOut()
      .then(() => {
        dispatch({
          type: "LOGOUT",
        });
        localStorage.clear();
      })
      .catch((error) => {
        dispatch({
          type: "LOGOUT_FAIL",
          uid: "",
          error: error,
        });
      });
  };
};

export const forgetPassword =
  (email, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch(forgetLoader(true));
    try {
      await User.forgetPassword({ email });
      toast.success("Please check your email and reset the password");
      dispatch(forgetLoader(false));
      onSuccess();
    } catch (error) {
      dispatch(forgetLoader(false));
      toast.error(
        error?.response?.data?.message?.includes("INTERNAL ASSERT FAILED")
          ? "User not found"
          : "Unable to send email"
      );
    }
  };

export const reRegisterSnapshot = (id, role) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    try {
      const unsubscribe = firebase
        .firestore()
        .collection("users")
        .doc(id)
        .onSnapshot(
          async (doc) => {
            try {
              const user = doc.data();
              let restaurantData = null;

              const isRoleValid =
                role === "kitchen-cook" ||
                role === "kitchen-admin" ||
                role === "kitchen-waiter" ||
                role === "restaurant" ||
                role === "admin" ||
                role === "sub-admin";

              if (!isRoleValid) {
                toast.error("You are not permitted to access the platform");
                dispatch(logout());
                return reject(new Error("Unauthorized role"));
              }

              const restaurantSubRole =
                role === "kitchen-cook" ||
                role === "kitchen-admin" ||
                role === "kitchen-waiter";
              if (restaurantSubRole) {
                if (user?.restaurantID) {
                  const restaurantDoc = await firebase
                    .firestore()
                    .collection("users")
                    .doc(user.restaurantID)
                    .get();

                  restaurantData = restaurantDoc.data();

                  if (restaurantData?.subScriptionStatus !== "subscribe") {
                    toast.error(
                      "Your restaurant subscription has expired. Resubscribe to access the platform."
                    );
                    dispatch(logout());
                    return reject(
                      new Error("Your restaurant subscription has expired.")
                    );
                  }

                  dispatch(getPermissions(doc.id));
                } else {
                  toast.error("No associated restaurant found for this user");
                  dispatch(logout());
                  return reject(
                    new Error("Missing restaurantID for this user")
                  );
                }
              }

              if (role === "restaurant" && user?.subscription?.subscriptionID) {
                await User.getSubscriptionStatus(
                  user.subscription.subscriptionID
                );
              }

              dispatch({
                type: "LOGIN_SUCCESS",
                user: { id: doc.id, ...user },
              });

              dispatch({
                type: "RESTAURANT_MEDIA",
                payload: {
                  images: restaurantSubRole
                    ? restaurantData?.restaurantImages
                    : user?.restaurantImages,
                  logo: restaurantSubRole
                    ? restaurantData?.restaurantLogo
                    : user?.restaurantLogo,
                  adminColor: restaurantSubRole
                    ? restaurantData?.adminColor
                    : user?.adminColor,
                  customerColor: restaurantSubRole
                    ? restaurantData?.customerColor
                    : user?.customerColor,
                  languages: restaurantSubRole
                    ? restaurantData?.languages
                    : user?.languages,
                  lightBg: restaurantSubRole
                    ? restaurantData?.customerBG
                    : user?.customerBG,
                  darkBg: restaurantSubRole
                    ? restaurantData?.customerDarkBG
                    : user?.customerDarkBG,
                },
              });

              dispatch({ type: "LOGIN_REQUEST_END" });

              resolve(user);
            } catch (err) {
              toast.error("An error occurred while processing restaurant data");
              dispatch(logout());
              reject(err);
            }
          },
          (error) => {
            toast.error("Error connecting to restaurant snapshot");
            reject(error);
          }
        );
      unsubscribeSnapshot = unsubscribe;
      return unsubscribe;
    } catch (err) {
      toast.error(
        "Unexpected error occured while getting restaurant information"
      );
      reject(err);
    }
  });
};

// export const reRegisterSnapshot = (id, role) => async (dispatch) => {
//   try {
//     const docSnap = await firebase
//       .firestore()
//       .collection("users")
//       .doc(id)
//       .get();

//     if (!docSnap.exists) {
//       toast.error("User not found");
//       dispatch(logout());
//       throw new Error("User document not found");
//     }

//     const user = docSnap.data();
//     let restaurantData = null;

//     const isRoleValid =
//       role === "kitchen-cook" ||
//       role === "kitchen-admin" ||
//       role === "kitchen-waiter" ||
//       role === "restaurant" ||
//       role === "admin" ||
//       role === "sub-admin";

//     if (!isRoleValid) {
//       toast.error("You are not permitted to access the platform");
//       dispatch(logout());
//       throw new Error("Unauthorized role");
//     }

//     const restaurantSubRole =
//       role === "kitchen-cook" ||
//       role === "kitchen-admin" ||
//       role === "kitchen-waiter";

//     if (restaurantSubRole) {
//       if (user?.restaurantID) {
//         const restaurantDoc = await firebase
//           .firestore()
//           .collection("users")
//           .doc(user.restaurantID)
//           .get();

//         restaurantData = restaurantDoc.data();

//         if (restaurantData?.subScriptionStatus !== "subscribe") {
//           toast.error(
//             "Your restaurant subscription has expired. Resubscribe to access the platform."
//           );
//           dispatch(logout());
//           throw new Error("Your restaurant subscription has expired.");
//         }

//         dispatch(getPermissions(docSnap.id));
//       } else {
//         toast.error("No associated restaurant found for this user");
//         dispatch(logout());
//         throw new Error("Missing restaurantID for this user");
//       }
//     }

//     if (role === "restaurant" && user?.subscription?.subscriptionID) {
//       await User.getSubscriptionStatus(user.subscription.subscriptionID);
//     }

//     dispatch({
//       type: "LOGIN_SUCCESS",
//       user: { id: docSnap.id, ...user },
//     });

//     dispatch({
//       type: "RESTAURANT_MEDIA",
//       payload: {
//         images: restaurantSubRole
//           ? restaurantData?.restaurantImages
//           : user?.restaurantImages,
//         logo: restaurantSubRole
//           ? restaurantData?.restaurantLogo
//           : user?.restaurantLogo,
//         adminColor: restaurantSubRole
//           ? restaurantData?.adminColor
//           : user?.adminColor,
//         customerColor: restaurantSubRole
//           ? restaurantData?.customerColor
//           : user?.customerColor,
//         languages: restaurantSubRole
//           ? restaurantData?.languages
//           : user?.languages,
//         lightBg: restaurantSubRole
//           ? restaurantData?.customerBG
//           : user?.customerBG,
//         darkBg: restaurantSubRole
//           ? restaurantData?.customerDarkBG
//           : user?.customerDarkBG,
//       },
//     });

//     dispatch({ type: "LOGIN_REQUEST_END" });

//     return user;
//   } catch (err) {
//     toast.error("An error occurred while logging in");
//     dispatch(logout());
//     throw err;
//   }
// };

export const getPermissions = (id) => (dispatch) => {
  try {
    const unsubscribe = firebase
      .firestore()
      .collection("permissions")
      .where("userID", "==", id)
      .limit(1)
      .onSnapshot(
        (querySnapshot) => {
          let permissionsData = {};
          querySnapshot.forEach((doc) => {
            permissionsData = { id: doc.id, ...doc.data() };
          });
          dispatch({
            type: "PERMISSIONS",
            payload: permissionsData,
          });
        },
        (error) => {
          console.error("Error fetching permissions data: ", error);
        }
      );

    return unsubscribe;
  } catch (error) {
    console.error("Error initializing permissions listener: ", error);
  }
};

export const updateColors = (id, payload) => async (dispatch) => {
  try {
    dispatch(colorLoader(true));

    await firebase
      .firestore()
      .collection("users")
      .doc(id)
      .update({ ...payload });

    dispatch(colorLoader(false));

    return Promise.resolve();
  } catch (error) {
    console.error("Error updating colors:", error);
    dispatch(colorLoader(false));
    return Promise.reject(error);
  }
};

const colorLoader = (data) => async (dispatch) => {
  dispatch({
    type: "COLOR_LOADER",
    payload: data,
  });
};

const Loader = (data) => async (dispatch) => {
  dispatch({
    type: "CATALOG_LOADER",
    payload: data,
  });
};

const forgetLoader = (data) => async (dispatch) => {
  dispatch({
    type: "FORGET_LOADER",
    payload: data,
  });
};
export const adminSignUp =
  (payload, password, onSuccess = (data) => {}) =>
  async (dispatch) => {
    firebase
      .auth()
      .createUserWithEmailAndPassword(payload.email, password)
      .then((data) => {
        firebase
          .firestore()
          .collection("users")
          .doc(data.user?.uid)
          .set({
            name: payload?.name,
            email: payload?.email,
            firsttimelogin: true,
            restaurantID: data?.user?.uid,
            isDeleted: false,
            status: "active",
            subScriptionStatus:
              payload?.paymentMethod === "easypaisa"
                ? "unsubscribe"
                : "subscribe",
            currency: "$",
            language: "en",
            type: "restaurant",
            restaurantLogo: "",
            socialProfiles: [
              {
                type: "facebook",
              },
              {
                type: "instagram",
              },
              {
                type: "tiktok",
              },
              {
                type: "discord",
              },
              {
                type: "youtube",
              },
              {
                type: "website",
              },
              {
                type: "linktree",
              },
              {
                type: "linkedIn",
              },
              {
                type: "snapchat",
              },
              {
                type: "twiter",
              },
            ],
            storeTiming: [
              { day: "Monday", startTime: "", endTime: "" },
              { day: "Tuesday", startTime: "", endTime: "" },
              { day: "Wednesday", startTime: "", endTime: "" },
              { day: "Thursday", startTime: "", endTime: "" },
              { day: "Friday", startTime: "", endTime: "" },
              { day: "Saturday", startTime: "", endTime: "" },
              { day: "Sunday", startTime: "", endTime: "" },
            ],
            createdAt: firebase.firestore.Timestamp.now(),
          })
          .then((res) => {
            onSuccess(data);
          })
          .catch((err) => {
            // dispatch(signupLoader(false));
            toast.error(err.message);
          });
      })
      .catch((err) => {
        // dispatch(signupLoader(false));
        toast.error(err.message);
      });
  };
export const CloseAlert = (id, onDismiss) => async (dispatch) => {
  try {
    await firebase
      .firestore()
      .collection("users")
      .doc(id)
      .update({
        firsttimelogin: false,
      })
      .then(() => onDismiss()); // Call onSuccess callback upon success
  } catch (error) {
    console.error("Error closing alert:", error);
  }
};

export const easypaisaSubscription =
  ({
    userData,
    paymentImage,
    request,
    startDate = "",
    onSuccess = () => {},
    onError = () => {},
  }) =>
  async () => {
    try {
      let toastMessage = "Subscription request";
      if (request !== "subscription") {
        toastMessage = `${
          request.charAt(0).toUpperCase() + request.slice(1)
        } subscription request`;
      }

      const docRef = firebase
        .firestore()
        .collection("subscriptions")
        .doc(userData?.uid);
      const docSnapshot = await docRef.get();

      // if (docSnapshot.exists) {
      //   throw new Error(`${toastMessage} already sent.`);
      // }

      let downloadURL = "";
      if (paymentImage) {
        const storageRef = firebase
          .storage()
          .ref(`subscriptionImages/${userData?.uid}/${paymentImage?.name}`);
        const uploadTaskSnapshot = await storageRef.put(paymentImage);
        downloadURL = await uploadTaskSnapshot.ref.getDownloadURL();
      }

      await firebase
        .firestore()
        .collection("subscriptions")
        .doc(userData?.uid)
        .set({
          ...userData,
          request,
          startDate: startDate,
          status: "pending",
          paymentImageUrl: downloadURL,
          createdAt: firebase.firestore.Timestamp.now(),
        });

      toast.success(`${toastMessage} sent successfully.`);
      onSuccess();
    } catch (error) {
      console.error(error.message || "An unknown error has occurred.");
      toast.error(error.message || "An unknown error has occurred.");
      onError();
    }
  };

export const checkAuthAndSendVerification = ({ onSuccess = () => {} }) => {
  return async () => {
    try {
      const user = firebase.auth().currentUser;

      if (user && !user.emailVerified) {
        await user.sendEmailVerification();
        toast.success("Verification Email has been sent");
        onSuccess();
      }
    } catch (error) {
      toast.error(`Error sending verification email: ${error.message}`);
    }
  };
};
